// Payroll calculation + classification — pure functions, no React/Supabase,
// verified by scripts/check-payroll-calc.mjs. Takes parsed muster rows
// (src/utils/musterParse.js), the employee master, and year-to-date leave
// sums, and decides per employee: the computed pay, the flags, and whether a
// payslip may be generated.
//
// The muster is authoritative for PAY: warnings (leave over allocation,
// leave-type conflicts) never change the figures — they exist so the muster
// can be corrected BEFORE slips are generated. Blockers stop a payslip.

export const WAGE_DIVISOR = 30;  // daily wage is gross/30 whatever the month length
export const L1_PER_MONTH = 4;   // enforced as a parser warning (l1_over_monthly)
export const CL_PER_YEAR = 8;    // April–March leave year
export const ML_PER_YEAR = 7;    // April–March leave year

export const round2 = (v) => Math.round(v * 100) / 100;

// Leave year runs April–March: the year of a Jan–Mar month started the
// previous April. Returns 'YYYY-04' for use as an inclusive lower bound.
export const leaveYearStart = (month) => {
  const [y, m] = month.split('-').map(Number);
  return m >= 4 ? `${y}-04` : `${y - 1}-04`;
};

// Active employees whose leave type is unset. The calculation refuses to run
// while this list is non-empty — nothing is assumed about anyone's leave.
export const missingLeaveType = (employees) =>
  (employees || []).filter((e) => e.is_active && !e.leave_type);

// ─── The calculation ─────────────────────────────────────────────────────────
//   daily_wage       = gross_monthly / 30     (always 30, rounded to 2dp)
//   days_unpaid      = LWP + A                (display/storage figure)
//   unpaid_deduction = (LWP + A + dashes) * daily_wage, rounded to the
//                      NEAREST WHOLE RUPEE. Dashes deduct per the original
//                      spec (unpaid_days = LWP + A + dashes): a mid-month
//                      joiner/leaver still gets no SLIP, but their computed
//                      pay feeds the month total (decision 05-09-2026).
//                      Rounding lands on the deduction, never the net, so
//                      gross − total deductions = net closes exactly; gross
//                      is whole, so the net is whole whenever the statutory
//                      deductions are whole.
//   gross_earned     = gross_monthly - unpaid_deduction
//   net_pay          = gross_earned - PF - ESIC - professional tax
// The stored daily_wage keeps its paise (583.33), so days × daily_wage can
// sit up to 50p from the stored deduction — that is the rounding, working
// as intended. Note the /30 convention's quirk in a 31-day month: 11 days
// worked + 20 dashes deducts 20 × wage from a 30-wage gross, i.e. 10 days'
// wage-equivalent — the same convention that pays a full 31-day month
// exactly gross, not 31 × wage.
// PF/ESIC/PT come from the employee record and are NOT pro-rated. A NULL
// deduction on the record is treated as 0 (many employees genuinely have no
// PT/ESIC) but is reported in missingDeductions so the screen can warn.
export function computePay(counts, emp) {
  const daysPaid = counts.fd + counts.l1 + counts.cl + counts.ml + counts.wo;
  const daysUnpaid = counts.lwp + counts.absent;
  const daysNotEmployed = counts.dash;
  const dailyWage = round2(emp.gross_monthly / WAGE_DIVISOR);
  let unpaidDeduction = Math.round((daysUnpaid + daysNotEmployed) * dailyWage); // whole rupees — see above
  // Full-absence bounds (05-09-2026, the Babu Bhuyan case): the /30 divisor
  // over- or under-shoots at month edges — 31 unpaid days would deduct 31
  // wages from a 30-wage gross (earned goes NEGATIVE), while 28 unpaid days
  // in February would leave 2 days' wage for zero work. Two rules:
  //   * no paid day at all  -> earned is exactly 0 (deduction = gross);
  //   * otherwise the deduction never exceeds gross (earned floors at 0).
  // Net can still go negative below — the fixed statutory deductions come
  // off an earned gross of 0 — and that raises the negative_net warning.
  if (daysPaid === 0) unpaidDeduction = emp.gross_monthly;
  else if (unpaidDeduction > emp.gross_monthly) unpaidDeduction = emp.gross_monthly;
  const grossEarned = round2(emp.gross_monthly - unpaidDeduction);
  const pf = emp.pf ?? 0;
  const esic = emp.esic ?? 0;
  const professionalTax = emp.professional_tax ?? 0;
  const netPay = round2(grossEarned - pf - esic - professionalTax);
  const missingDeductions = [
    emp.pf == null && 'PF',
    emp.esic == null && 'ESIC',
    emp.professional_tax == null && 'Professional Tax',
  ].filter(Boolean);
  return { daysPaid, daysUnpaid, daysNotEmployed, dailyWage, unpaidDeduction, grossEarned, pf, esic, professionalTax, netPay, missingDeductions };
}

// ─── Designation fill ────────────────────────────────────────────────────────
// The muster carries a Designation per employee; the employee record is the
// authority for it (it is what the slip prints). So the upload only ever
// FILLS A BLANK: an employee matched in the muster whose record has no
// designation takes the muster's. A record that already has one is never
// touched, whatever the muster says. Pure — the upload shows this list in the
// write plan and then writes exactly it.
// rows: classified rows (each with .employee | null and the muster's
// .designation). Returns [{ employeeId, code, name, designation }], one per
// employee, in muster order.
export function designationFills(rows) {
  const clean = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');
  const seen = new Set();
  const fills = [];
  for (const r of rows || []) {
    const e = r.employee;
    if (!e || seen.has(e.id)) continue;
    const fromMuster = clean(r.designation);
    if (!fromMuster || clean(e.designation)) continue;
    seen.add(e.id);
    fills.push({ employeeId: e.id, code: e.employee_code, name: e.name, designation: fromMuster });
  }
  return fills;
}

// ─── Classification ──────────────────────────────────────────────────────────
// rows: parsed muster rows. employees: the full employee master (active and
// inactive). ytd: Map employee_id -> { cl, ml } summed from STORED attendance
// months of the same leave year BEFORE this month (never including it — a
// re-upload of the month must not double-count itself).
//
// Adds the flags only the database can know, then decides status:
//   blocked  — any blocker flag: no payslip until resolved
//   excluded — payment type Cash (08-09-2026): no payslip BY DESIGN, not a
//              data problem. Pay is computed for the month total and stored
//              on payroll_attendance exactly like a blocked row, so there is
//              no payslips row, hence no PDF and nothing the send flow can
//              ever pick up — structural, not gated. A Cash employee WITH a
//              blocker stays 'blocked' so the data problem remains visible.
//   ok       — payslip may be generated
// Returns { rows, missingFromMuster } where missingFromMuster lists ACTIVE
// employees with no muster row (M005/M009 expected — info, not an error;
// inactive leavers are silent by design).
export function classifyRows({ rows, employees, ytd }) {
  const byCode = new Map(employees.map((e) => [e.employee_code, e]));

  const out = rows.map((r) => {
    const emp = byCode.get(r.musterEmployeeId) || null;
    const flags = [...r.flags];
    let pay = null;

    if (!emp) {
      flags.push({
        code: 'not_in_database',
        level: 'blocker',
        message: 'Not in the employee database — add them in the Employees tab, then re-upload the muster. Never skipped silently.',
      });
    } else {
      if (!emp.is_active) {
        flags.push({
          code: 'inactive_employee',
          level: 'blocker',
          message: 'Marked inactive (leaver) in the employee database but has attendance this month — reactivate them in the Employees tab or correct the muster.',
        });
      }
      if (emp.gross_monthly == null) {
        flags.push({
          code: 'no_salary_on_record',
          level: 'blocker',
          message: 'No Gross Monthly salary on the employee record — set it in the Employees tab before a payslip can be computed.',
        });
      }

      // WO employees receive no L1, and vice versa. Both codes are PAID, so
      // pay is identical either way — warning, not blocker.
      if (emp.leave_type === 'WO' && r.counts.l1 > 0) {
        flags.push({
          code: 'leave_type_conflict',
          level: 'warning',
          message: `${r.counts.l1} L1 day(s) in the muster, but this employee is WO type (weekly offs, no L1). Pay is unaffected — correct the muster or the employee record.`,
        });
      }
      if (emp.leave_type === 'L1' && r.counts.wo > 0) {
        flags.push({
          code: 'leave_type_conflict',
          level: 'warning',
          message: `${r.counts.wo} WO day(s) in the muster, but this employee is L1 type (no weekly offs). Pay is unaffected — correct the muster or the employee record.`,
        });
      }

      // CL/ML over the April–March allocation: warn, never change pay.
      const y = ytd.get(emp.id) || { cl: 0, ml: 0 };
      if (y.cl + r.counts.cl > CL_PER_YEAR) {
        flags.push({
          code: 'cl_over_year',
          level: 'warning',
          message: `CL reaches ${y.cl + r.counts.cl} of ${CL_PER_YEAR} for the April–March leave year (${y.cl} in earlier stored months + ${r.counts.cl} this month). Pay unchanged — if the excess should be LWP, correct the muster before generating.`,
        });
      }
      if (y.ml + r.counts.ml > ML_PER_YEAR) {
        flags.push({
          code: 'ml_over_year',
          level: 'warning',
          message: `ML reaches ${y.ml + r.counts.ml} of ${ML_PER_YEAR} for the April–March leave year (${y.ml} in earlier stored months + ${r.counts.ml} this month). Pay unchanged — if the excess should be LWP, correct the muster before generating.`,
        });
      }

      if (emp.gross_monthly != null) {
        pay = computePay(r.counts, emp);
        if (pay.missingDeductions.length) {
          flags.push({
            code: 'missing_deduction_figures',
            level: 'warning',
            message: `No ${pay.missingDeductions.join(' / ')} on the employee record — treated as 0 in the calculation. Set the figure(s) in the Employees tab if that is wrong.`,
          });
        }
        // Fixed statutory deductions can exceed heavily-docked earnings
        // (many unpaid/not-employed days). Never hidden, never clamped.
        if (pay.netPay < 0) {
          flags.push({
            code: 'negative_net',
            level: 'warning',
            message: `Net pay computes to ${pay.netPay} — the fixed PF/ESIC/PT exceed this month's earned gross. Resolve with HR before paying.`,
          });
        }
      }
    }

    const isCash = !!emp && String(emp.payment_type || '').trim().toLowerCase() === 'cash';
    if (isCash) {
      flags.push({
        code: 'cash_payment',
        level: 'info',
        message: 'Payment type Cash — calculated for the month total and stored on attendance; no payslip, no PDF, nothing to send.',
      });
    }

    const status = flags.some((f) => f.level === 'blocker') ? 'blocked' : (isCash ? 'excluded' : 'ok');
    return { ...r, employee: emp, flags, pay, status };
  });

  const musterIds = new Set(rows.map((r) => r.musterEmployeeId));
  const missingFromMuster = employees.filter((e) => e.is_active && !musterIds.has(e.employee_code));

  return { rows: out, missingFromMuster };
}
